<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="!isModal"></column-header>
    <div :class="(!isModal ? 'tabulator-parent' : '')">
      <div class="row" v-if="!isModal">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-truck"></i> {{ _('Delivery methods') }}</h3>
        </div>
      </div>
      <div class="modal-header" v-else>
        <h3><i class="fa fa-fw fa-truck"></i> {{ _('Delivery methods') }}</h3>
      </div>
      <div :class="(isModal ? 'tabulator-parent modal-body' : '')">
        <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
        <div class="tab-content">
          <transition-group name="fade">
            <div v-bind="tabPanel('settings')" class="tab-pane active" id="settings" v-if="isTabActive('settings')" key="settings">
              <div class="row">
                <div class="col-xs-12">
                  <h3>{{ _('Default level of required customer information') }}</h3>
                </div>
              </div>
              <div class="row">
                <div class="col-xs-6">
                  <div class="form-group">
                    <label for="requiredInformation">{{ _('Required customer information') }}</label>
                    <select class="form-control" id="requiredInformation" v-model="settings.requiredinfo">
                      <option value="minimal">{{ _('Name and email') }}</option>
                      <option value="minimalphone">{{ _('Name, email and phone') }}</option>
                      <option value="default">{{ _('Full details') }}</option>
                      <option v-if="isFeatureEnabled('strongauthentication') || settings.requiredinfo === 'authentication'" value="authentication">{{ _('Strong authentication') }} (€)</option>
                    </select>
                    <p><i>{{ _('This setting affects new products.') }}</i></p>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-xs-12">
                  <h3>{{ _('Shipit') }}</h3>
                </div>
              </div>
              <div class="row">
                <div class="col-xs-12">
                  <div class="form-group" data-search="use-shipit">
                    <label>
                      <input type="checkbox" v-model="settings.shipit" true-value="1" false-value="0" /> {{ _('Use Shipit') }}
                    </label>
                  </div>
                  <div class="form-group" data-search="shipit-api-key">
                    <label for="shipit_apikey">{{ _('Shipit API key') }}</label>
                    <input type="text" class="form-control" :placeholder="_('Enter your Shipit API key')" v-model="settings.shipit_apikey" />
                  </div>
                  <div class="form-group" data-search="shipit-api-secret">
                    <label for="shipit_apisecret">{{ _('Shipit API secret') }}</label>
                    <input type="text" class="form-control" id="netvisorOrganizationId" :placeholder="_('Enter your Shipit API secret key')" v-model="settings.shipit_apisecret" />
                  </div>
                  <div class="form-group" data-search="shipit-sender-address">
                    <label for="shipit_senderaddress">{{ _('Shipit sender address') }}</label>
                    <p v-if="shipitAddress">{{ shipitAddress.company }}, {{ shipitAddress.address }}, {{ shipitAddress.zip }} {{ shipitAddress.city }}</p>
                    <p v-else>{{ _('Use the address in Company Profile') }}</p>
                    <button class="btn btn-info" v-on:click="editShipitAddressDialog"><i class="fa fa-fw fa-pencil"></i> {{ _('Edit address') }}</button>
                    <button v-if="shipitAddress" class="btn btn-default" v-on:click="removeShipitAddress"><i class="fa fa-fw fa-trash"></i> {{ _('Use default address') }}</button>
                  </div>
                </div>
              </div>
            </div>
            <div v-bind="tabPanel('deliverymethods')" class="tab-pane active" id="deliverymethods" v-if="isTabActive('deliverymethods')" key="deliverymethods">
              <div class="row">
                <div class="col-xs-12">
                  <button type="button" class="btn btn-success pull-right" id="addWarehouse" v-on:click="addDeliverymethodDialog"><i class="fa fa-fw fa-plus"></i> {{ _('Add delivery method') }}</button>
                  <h3>{{ _('Delivery methods') }}</h3>
                </div>
              </div>
              <Datatable :url="deliverymethodsUrl" :options="deliverymethodTableOptions" ref="datatableDeliverymethods" />
            </div>
            <div v-bind="tabPanel('deliverymethodgroups')" class="tab-pane active" id="deliverymethodgroups" v-if="isTabActive('deliverymethodgroups')" key="deliverymethodgroups">
              <div class="row">
                <div class="col-xs-12">
                  <button type="button" class="btn btn-success pull-right" id="addWarehouse" v-on:click="addDeliverymethodGroupDialog"><i class="fa fa-fw fa-plus"></i> {{ _('Add delivery method group') }}</button>
                  <h3>{{ _('Delivery method groups') }}</h3>
                </div>
              </div>
              <Datatable :url="deliverymethodGroupsUrl" :options="deliverymethodGroupsTableOptions" ref="datatableDeliverymethodGroups" />
            </div>
          </transition-group>
        </div>
      </div>
    </div>
    <nav :class="(isModal ? 'modal-footer' : 'save-bar')" v-if="isSaveBarVisible">
      <div class="flex-row">
        <button v-if="isSaveButtonVisible" class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        <button v-if="isCloseButtonVisible" class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'
import formatters from '~/lib/formatters'
import settingseditors from '~/lib/settingseditors'
import { Datatable } from '#components'
const AddressDetails = defineAsyncComponent(() => import('~/components/modals/AddressDetails'))
const DeliverymethodDetails = defineAsyncComponent(() => import('~/components/DeliverymethodDetails'))
const DeliverymethodGroupDetails = defineAsyncComponent(() => import('~/components/DeliverymethodGroupDetails'))

export default {
  data () {
    const $this = this
    const _ = useNuxtApp().$translate
    return {
      shipitAddress: null,
      localSettings: null,
      activeTab: 'settings',
      deliveryMethods: [],
      deliveryMethodGroups: [],
      deliverymethodTableOptions: {
        columns: [
        {
          title: 'ID',
          field: 'id',
          width: 70,
          responsive: 0
        },
        {
          title: _('Name'),
          field: 'name',
          widthGrow: 4,
          responsive: 0
        },
        {
          title: _('Shipit'),
          field: 'shipitmethod.name',
          widthGrow: 3,
          responsive: 1,
          headerSort: false
        },
        {
          title: _('Type'),
          field: 'internal',
          widthGrow: 2,
          responsive: 1,
          headerSort: false,
          formatter: this.deliveryTypeFormatter()
        },
        {
          title: _('Price'),
          field: 'price',
          widthGrow: 2,
          responsive: 1,
          hozAlign: 'right',
          formatter: this.shipitPriceFormatter()
        },
        {
          title: _('Shipment dimensions'),
          field: 'maxheight',
          widthGrow: 2,
          responsive: 1,
          formatter: this.shipmentDimensionsFormatter(),
          headerSort: false
        },
        {
          title: _('Tags'),
          field: 'sharedTag',
          widthGrow: 3,
          responsive: 1,
          formatter: this.tagFormatter(),
          headerSort: false
        },
        ],
        initialSort: [{
          column: 'name',
          dir: 'asc'
        }],
        rowClick (e, row) {
          const id = row.getCell('id').getValue()
          const name = row.getCell('name').getValue()
          $this.editDeliverymethod(id, name)
        }
      },
      deliverymethodGroupsTableOptions: {
        columns: [
        {
          title: 'ID',
          field: 'id',
          width: 70,
          responsive: 0
        },
        {
          title: _('Name'),
          field: 'name',
          widthGrow: 4,
          responsive: 0
        },
        ],
        initialSort: [{
          column: 'name',
          dir: 'asc'
        }],
        rowClick (e, row) {
          const id = row.getCell('id').getValue()
          const name = row.getCell('name').getValue()
          $this.editDeliverymethodGroup(id, name)
        }
      }
    }
  },
  mixins: [components, formatters, settingseditors],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'deliverymethods', label: this._('Delivery methods') },
        { id: 'deliverymethodgroups', label: this._('Delivery method groups') },
        { id: 'settings', label: this._('Delivery settings') }
      ])
    },
    deliverymethodsUrl () {
      return this.baseDomain + this.shopId + '/deliverymethods.json?shipitmethods=true&tags=true&apikey=' + this.apiKey + '&rawformat=true'
    },
    deliverymethodGroupsUrl () {
      return this.baseDomain + this.shopId + '/deliverymethodgroups.json?apikey=' + this.apiKey + '&rawformat=true'
    },
    editorConfig () {
      return {
        toolbar:
          [
            ['Format', 'Bold', 'Italic', '-', 'NumberedList', 'BulletedList', 'Outdent', 'Indent', '-', 'Link', 'Unlink'],
            ['JustifyLeft', 'JustifyCenter', 'JustifyRight'],
            ['Undo', 'Redo'],
            ['Source'],
            ['UIColor']
          ],
        height: '200px',
        language: this.shortLocale,
        allowedContent: true,
        extraPlugins: 'justify'
      }
    },
    isSaveBarVisible () {
      return this.isSaveButtonVisible || this.isCloseButtonVisible
    },
    isSaveButtonVisible () {
      return this.activeTab === 'settings'
    },
    isCloseButtonVisible () {
      return this.isModal
    }
  },
  methods: {
    editShipitAddressDialog () {
      this.showModal('shipitaddressdetails', AddressDetails, {
        props: {
          value: this.shipitAddress
        },
        events: {
          save: this.setShipitAddress
        },
        handlers: {},
        width: 1000
      })
    },
    setShipitAddress (data) {
      this.shipitAddress = data
    },
    removeShipitAddress () {
      this.shipitAddress = null
    },
    addDeliverymethodDialog () {
      this.showDialog({
        title: this._('Add delivery method'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['name'],
            handler: (data) => {
              this.addDeliverymethod(data.name)
            }
          }
        ],
        fields: [
          {
            title: this._('Name'),
            type: 'text',
            name: 'name'
          }
        ]
      })
    },
    async addDeliverymethod (name) {
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/deliverymethods.json?apikey=' + this.apiKey, { name, settings_id: 1 })
      } catch(e) {
        console.log(e)
        this.showError(e)
        return
      }
      this.editDeliverymethod(response.data, name)
    },
    editDeliverymethod (id, name, activeTab, skipState) {
      if (this.isModal) {
        this.showModal('deliverymethoddetails', DeliverymethodDetails, {
          props: {
            baseDomain: this.baseDomain,
            shopId: this.shopId,
            apiKey: this.apiKey,
            component: {
              type: 'DeliverymethodDetails',
              initData: {
                deliverymethodId: id,
                modal: true
              }
            },
            className: 'modal-content'
          },
          events: {},
          handlers: {},
          width: 1000
        })
      } else {
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'DeliverymethodDetails',
          title: this._('Delivery method') + ' ' + id + (name ? ': ' + name : ''),
          width: 6,
          data: {
            deliverymethodId: id,
            activeTab
          },
          skipState
        })
      }
    },
    close () {
      if (this.component && this.component.initData && this.component && this.component.initData.modal) {
        this.$emit('close')
      } else {
        this.EventBus.$emit('hideDetailsColumn', {
          shopId: this.shopId
        })
      }
    },
    loadDeliverymethods () {
      return new Promise(async (resolve) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/deliverymethods.json?apikey=' + this.apiKey)
        } catch(e) {
          this.showError(e)
          resolve([])
          return
        }
        this.deliveryMethods = response.data
        resolve(this.deliveryMethods)
      })
    },
    addDeliverymethodGroupDialog () {
      this.showDialog({
        title: this._('Add delivery method group'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['name'],
            handler: (data) => {
              this.addDeliverymethodGroup(data.name)
            }
          }
        ],
        fields: [
          {
            title: this._('Name'),
            type: 'text',
            name: 'name'
          }
        ]
      })
    },
    async addDeliverymethodGroup (name) {
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/deliverymethodgroups.json?apikey=' + this.apiKey, { name })
      } catch(e) {
        console.log(e)
        this.showError(e)
        return
      }
      this.editDeliverymethodGroup(response.data, name)
    },
    editDeliverymethodGroup (id, name, activeTab, skipState) {
      if (this.isModal) {
        this.showModal('deliverymethodgroupdetails', DeliverymethodGroupDetails, {
          props: {
            baseDomain: this.baseDomain,
            shopId: this.shopId,
            apiKey: this.apiKey,
            component: {
              type: 'DeliverymethodGroupDetails',
              initData: {
                deliverymethodgroupId: id,
                modal: true
              }
            },
            className: 'modal-content'
          },
          events: {},
          handlers: {},
          width: 1000
        })
      } else {
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'DeliverymethodGroupDetails',
          title: this._('Delivery method group') + ' ' + id + (name ? ': ' + name : ''),
          width: 6,
          data: {
            deliverymethodgroupId: id,
            activeTab
          },
          skipState
        })
      }
    },
    loadDeliverymethodGroups () {
      return new Promise(async (resolve) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/deliverymethodgroups.json?apikey=' + this.apiKey)
        } catch(e) {
          this.showError(e)
          resolve([])
          return
        }
        this.deliveryMethodGroups = response.data
        resolve(this.deliveryMethodGroups)
      })
    },
    loadShipitAddress () {
      return new Promise(async (resolve) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/addresses/shipit.json')
        } catch(e) {
          this.shipitAddress = null
          resolve(null)
          return
        }
        this.shipitAddress = response.data
        resolve(response.data)
      })
    },
    getSaveData () {
      const exportFields = [
        'id', 
        'requiredinfo',
        'shipit',
        'shipit_apikey',
        'shipit_apisecret'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.settings[e], a), {})
      data.deliverymethods = 1
      return data
    },
    saveShipitAddress (address) {
      return new Promise(async (resolve) => {
        let response
        let data = Object.assign({}, address)
        data.type = 'shipit'
        try {
          if (!address) {
            await axios.delete(this.baseDomain + this.shopId + '/addresses/shipit.json?apikey=' + this.apiKey, data)
          } else {
            await axios.put(this.baseDomain + this.shopId + '/addresses/shipit.json?apikey=' + this.apiKey, data)
          }
        } catch(e) {
          resolve()
          return
        }
        resolve()
      })
    },
    async save (payment) {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/settings/1.json?apikey=' + this.apiKey, data)
        } else {
          this.saving = false
          return
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      try {
        await this.saveShipitAddress(this.shipitAddress)
      } catch(e) {
        // uh oh
      }
      this.saving = false
      this.saved = true
      this.storeState()
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.settings)
      this.close()
    },
    reloadDeliverymethodsList () {
      if (this.$refs && this.$refs.datatableDeliverymethods) {
        this.$refs.datatableDeliverymethods.reload()
      }
    },
    reloadDeliverymethodGroupsList () {
      if (this.$refs && this.$refs.datatableDeliverymethodGroups) {
        this.$refs.datatableDeliverymethodGroups.reload()
      }
    }
  },
  components: {
    Datatable
  },
  async mounted () {
    this.localSettings = Object.assign({}, this.settings)
    if (parseInt(this.settings.shipit)) {
      this.loadShipitAddress()
    }
    this.storeState()
    if (!this.events) {
      let events = {}
      events['deliverymethodUpdate'] = (message) => {
        return this.reloadDeliverymethodsList()
      }
      events['deliverymethodgroupUpdate'] = (message) => {
        return this.reloadDeliverymethodGroupsList()
      }
      events['tagUpdate'] = (message) => {
        return this.reloadDeliverymethodsList()
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>